import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BookMockup } from "./BookMockup";
import { CtaButton } from "./CtaButton";
import { priceCta } from "@/config/site";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

// Stoupající zlatá linie – motiv převzatý z obálky knihy.
const GoldLine = () => (
  <svg
    viewBox="0 0 900 420"
    fill="none"
    aria-hidden="true"
    className="pointer-events-none absolute bottom-0 right-0 w-[720px] max-w-none opacity-[0.16]"
  >
    <motion.path
      d="M30 400 L230 320 L390 350 L540 200 L660 240 L870 40"
      stroke="#c4a277"
      strokeWidth="1.5"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2.4, delay: 0.9, ease: "easeInOut" }}
    />
    {[
      [30, 400],
      [230, 320],
      [390, 350],
      [540, 200],
      [660, 240],
      [870, 40],
    ].map(([cx, cy], i) => (
      <motion.circle
        key={i}
        cx={cx}
        cy={cy}
        r="7"
        fill="#c4a277"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.9 + i * 0.35, duration: 0.3 }}
      />
    ))}
  </svg>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="grain relative flex min-h-svh items-center overflow-hidden bg-navy"
      data-testid="hero-section"
    >
      {/* ambientní jantarový nádech západu slunce */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-1/3 left-1/2 h-[80vh] w-[120vw] -translate-x-1/2 rounded-[100%] bg-[radial-gradient(ellipse_at_center,rgba(217,123,72,0.22),transparent_60%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-[radial-gradient(75%_100%_at_50%_100%,rgba(166,90,58,0.14),transparent_70%)]"
      />
      <GoldLine />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-28 pt-36 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-20 lg:pt-28">
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.3em] text-copper"
          >
            <span className="h-px w-10 bg-copper/60" aria-hidden="true" />
            Proměna muže
          </motion.p>

          <h1 className="font-serif text-5xl font-medium leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.35}>40 principů</MaskedLine>
            <MaskedLine delay={0.5}>
              lepšího <span className="italic text-copper">muže</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
          >
            Praktický průvodce osobním rozvojem, sebevědomím a vztahy
          </motion.p>

          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-9 max-w-xl border-l-2 border-copper/70 pl-6 font-serif text-xl italic leading-snug text-cream/90 sm:text-2xl"
          >
            „Nejde o to stát se mužem, kterého obdivují ostatní. Jde o to stát se mužem, kterého si
            sám vážíš.“
          </motion.blockquote>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-11"
          >
            <CtaButton event="hero_cta" location="hero" size="lg" testId="hero-cta">
              {priceCta}
            </CtaButton>
            <p className="mt-4 text-xs tracking-wide text-cream/50 sm:text-sm">
              PDF e-book · jednorázová platba · ihned po zaplacení
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: bookY }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <BookMockup />
        </motion.div>
      </div>
    </section>
  );
};
